import React from "react";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import axios from "axios";
import { cloneDeep, pick, keys, difference, isArray, forIn, get, setWith, split, set, isObject } from "lodash";
import Papa from "papaparse";
import { css } from "glamor";
import { db, auth } from "./firebase";
import settings from "../../settings.json";

export const countWords = s => {
  let result = s;
  result = s.replace(/(^\s*)|(\s*$)/gi, ""); // exclude  start and end white-space
  result = s.replace(/[ ]{2,}/gi, " "); // 2 or more space to 1
  result = s.replace(/\n /, "\n"); // exclude newline with a start spacing
  return result.split(" ").filter(str => str !== "").length;
};

export const mergeExcludingExtraKeys = (base, trans) => {
  const original = cloneDeep(base);
  const newValues = cloneDeep(trans);
  const newTranslationObject = {};

  keys(original).forEach(key => {
    if (key === "pages") {
      keys(original[key]).forEach(page => {
        set(newTranslationObject, [key, page], pick(get(newValues, [key, page]), keys(original[key][page])));
      });
    } else {
      set(newTranslationObject, [key], pick(get(newValues, [key]), keys(original[key])));
    }
  });
  return newTranslationObject;
};

export const sendValidationEmail = () => {
  const firebaseUser = auth.currentUser;

  if (firebaseUser != null) {
    firebaseUser.reload();
    if (!firebaseUser.emailVerified) {
      firebaseUser
        .sendEmailVerification()
        .then(() => toast.success("Email Sent!"))
        .catch(err => {
          toast.error("Email not Sent!");
          console.error(err);
        });
    } else {
      toast.success("Email already validated");
    }
  }
};

export const deleteUser = user => {
  db.doc(`users/${user.email}`)
    .delete()
    .then(() =>
      toast.update("deletePrompt", {
        render: `The user ${user.email} was succesfully deleted from the app`,
        type: toast.TYPE.SUCCESS,
        autoClose: 5000,
        className: css({
          transform: "rotateY(360deg)",
          transition: "transform 0.6s"
        })
      })
    )
    .catch(err => {
      console.error(err);
      toast.error("There was an error while trying to delete the user");
    });
};

export const saveAccess = (email, access, canSeeFinance, isAdmin, canSave, canPublish, canDownloadBase) => {
  if (!access || access.length < 1 || !email) {
    toast.error("No Access Selected");
    return;
  }

  const defaultValue = {
    brand: access[0].brand,
    market: access[0].markets[0].market,
    language: access[0].markets[0].languages[0].language || access[0].markets[0].languages[0]
  };

  db.doc(`users/${email}`)
    .set(
      {
        access: JSON.stringify(access),
        canSeeFinance: !!canSeeFinance,
        isAdmin: !!isAdmin,
        defaultValue,
        canSave: !!canSave,
        canPublish: !!canPublish,
        canDownloadBase: !!canDownloadBase
      },
      { merge: true }
    )
    .then(() => toast.success("Access saved"))
    .catch(err => {
      console.error(err);
      toast.error("Problem while saving access");
    });
};

export const parseAccess = (access, currentSelection) => {
  const { brand } = currentSelection;
  let { market, language } = currentSelection;
  const brands = [];
  let pages = null;
  const markets = [];
  const languages = [];

  access.forEach(brandItem => {
    if (!brandItem.brand) return;

    brands.push(brandItem.brand);

    if (brand && brand.toLowerCase() === brandItem.brand.toLowerCase()) {
      if (brandItem.pages) {
        pages = [];
        brandItem.pages.forEach(page => {
          pages.push(page);
        });
      }
      brandItem.markets.forEach(marketItem => {
        markets.push(marketItem.market);
        if (!market) market = marketItem.market;
        if (market && market.toLowerCase() === marketItem.market.toLowerCase())
          marketItem.languages.forEach(languageItem => {
            languages.push(languageItem.language || languageItem);
          });
      });
    }
  });

  if (!language) language = isObject(languages[0]) ? languages[0].language : languages[0];

  return {
    brands,
    markets,
    languages,
    pages,
    brand,
    market,
    language
  };
};

const downloadObjectAsCsv = (csvFile, filename) => {
  const blob = new Blob([`\uFEFF${csvFile}`], { type: "text/csv;charset=utf-8;" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, "export.csv");
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const exportToCSV = (brand, market, language) => {
  const { API_URL } = settings;
  toast.info("Building the CSV, please be patient...", { toastId: "csvfile", autoClose: false });

  axios
    .get(`${API_URL}/csvJson`, { params: { brand, market, language } })
    .then(jsonString => {
      if (jsonString.data) {
        const csvString = Papa.unparse(jsonString.data, {
          quotes: false,
          quoteChar: '"',
          escapeChar: '"',
          delimiter: ",",
          header: true,
          newline: "\r\n",
          skipEmptyLines: "greedy",
          columns: ["PAGE", "MODULE", "FIELD", "BASE", "TRANSLATION"]
        });
        downloadObjectAsCsv(csvString, `${language}$${market}$${brand}`);
        toast.update("csvfile", {
          render: "CSV ready!",
          type: toast.TYPE.SUCCESS,
          autoClose: 5000,
          className: css({
            transform: "rotateY(360deg)",
            transition: "transform 0.6s"
          })
        });
      } else {
        toast.update("csvfile", {
          render: "Error!",
          type: toast.TYPE.ERROR,
          autoClose: 5000,
          className: css({
            transform: "rotateY(360deg)",
            transition: "transform 0.6s"
          })
        });
      }
    })
    .catch(error => {
      toast.error("Error generating csv");
      console.error(error);
    });
};

const GetBaseTranslation = (brand, market) =>
  new Promise((resolve, reject) => {
    db.doc(`brand/${brand.toLowerCase()}/market/${market.toLowerCase()}`)
      .get()
      .then(dataRef => {
        const data = dataRef.data();
        if (!data || !data.base)
          reject(new Error("Sorry there was an issue while trying to find a base for that market"));
        resolve(data.base);
      })
      .catch(err => {
        reject(err);
      });
  });

export const GetBaseDownloadUrl = (brand, market) => {
  return new Promise((resolve, reject) => {
    db.doc(`brand/${brand.toLowerCase()}/market/${market.toLowerCase()}`)
      .get()
      .then(dataRef => {
        const data = dataRef.data();
        if (!data || !data.signedURL)
          reject(new Error("Sorry there was an issue while trying to find a download URL for that market"));
        resolve(data.signedURL);
      })
      .catch(err => {
        reject(err);
      });
  });
};

const ProcessNewTranslation = (results, brand, market) =>
  new Promise((resolve, reject) => {
    GetBaseTranslation(brand, market).then(base => {
      let ErrorLog = "";
      let fatalError = false;
      const ignoredRows = [];
      const newSavedObject = {};
      const headers = ["PAGE", "FIELD", "MODULE", "BASE", "TRANSLATION"];

      const missing = difference(headers, results.meta.fields);
      const notExpected = difference(results.meta.fields, headers);

      if (missing.length > 0) {
        fatalError = true;
        reject(
          new Error(
            `The column/s '${missing.join("', '").replace(/, ([^,]*)$/, " and $1'")} is/are missing from the file
            I am expecting: 'PAGE', 'MODULE', 'FIELD', 'BASE' and 'TRANSLATION'.`
          )
        );
      }

      if (notExpected.filter(Boolean).length) {
        fatalError = true;
        reject(
          new Error(
            `The column/s '${notExpected.join("', '").replace(/, ([^,]*)$/, " and $1'")} is/are not expected. 
            I can't accept unknown columns
            The columns I am expecting are: 'PAGE', 'MODULE', 'FIELD', 'BASE' and 'TRANSLATION'.`
          )
        );
      }

      if (isArray(results.data) && !fatalError)
        results.data.forEach((row, index) => {
          let errorFound = false;
          forIn(row, (value, key) => {
            if (!key && value) {
              ErrorLog += `\r\n
[ERROR] -- there is a value in a column with no header
\tvalue: ${value}
\trow: ${index + 2}`;

              ignoredRows.push(index + 2);
              errorFound = true;
            }
          });
          if (!errorFound && row.TRANSLATION) {
            if (row.PAGE.toLowerCase() === "global") {
              const globalModuleName = get(base, `global.${row.MODULE}.moduleName`);

              if (globalModuleName) {
                const currentBaseText = get(base, `global.${row.MODULE}.${row.FIELD}`);
                const newBaseText = row.BASE;
                if (!currentBaseText) {
                  ErrorLog += `\r\n[ERROR] -- There is no base for the given key, this might be because you are using an old csv file, or a csv generated for another market
                  \r\n\tPage Name: global
                  \r\n\tField Name: ${row.FIELD} 
                  \r\n\tModule Name: ${row.MODULE} 
                  \r\n\trow: ${index + 2}`;
                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else if (currentBaseText.trim().toLowerCase() !== newBaseText.trim().toLowerCase()) {
                  ErrorLog += `\r\n
[ERROR] -- The base text stored as a base for the given key is different, are you sure the file is correct? You might not be saving the file as csv UTF-8 format
\r\n\tOriginal base: ${currentBaseText}
\r\n\tUploaded base text: ${newBaseText}
\r\n\tPage Name: global
\r\n\tField Name: ${row.FIELD} 
\r\n\tModule Name: ${row.MODULE} 
\r\n\trow: ${index + 2}`;
                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else {
                  setWith(newSavedObject, `global.${row.MODULE}.${row.FIELD}`, row.TRANSLATION, Object);
                  setWith(newSavedObject, `global.${row.MODULE}.moduleName`, globalModuleName, Object);
                }
              } else {
                ErrorLog += `\r\n
[WARNING] -- I could not find the MODULE: "${row.MODULE}" in this configuration, Are you using an old csv export? 
\r\n\trow: ${index + 2}`;
                ignoredRows.push(index + 2);
                errorFound = true;
              }
            } else if (row.MODULE.length > 36) {
              const parsedId = split(row.MODULE, "-");
              const item = parsedId.pop();
              const moduleId = `${parsedId[0]}-${parsedId[1]}-${parsedId[2]}-${parsedId[3]}-${parsedId[4]}`;
              // CHECK IF MODULE EXISTS
              if (!get(base, ["pages", row.PAGE, moduleId])) {
                ErrorLog += `\r\n[WARNING] -- I could not find the MODULE: "${
                  row.MODULE
                }" in this configuration, Are you using an old csv export? 
                \r\n\trow: ${index + 2}`;
                ignoredRows.push(index + 2);
                errorFound = true;
              } else {
                const currentBaseText = get(base, ["pages", row.PAGE, moduleId, item.toString(), row.FIELD]);
                const newBaseText = row.BASE;
                if (!currentBaseText) {
                  ErrorLog += `\r\n[ERROR] -- There is no base for the given key, this might be because you are using an old csv file, or a csv generated for another market
                  \r\n\tPage Name: ${row.PAGE}
                  \r\n\tField Name: ${row.FIELD} 
                  \r\n\tModule Name: ${row.MODULE} 
                  \r\n\trow: ${index + 2}`;
                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else if (currentBaseText.trim().toLowerCase() !== newBaseText.trim().toLowerCase()) {
                  ErrorLog += `\r\n
[ERROR] -- The base text stored as a base for the given key is different, are you sure the file is correct? You might not be saving the file as csv UTF-8 format
\r\n\tOriginal base: ${currentBaseText}
\r\n\tUploaded base text: ${newBaseText}
\r\n\tPage Name: ${row.PAGE}
\r\n\tField Name: ${row.FIELD} 
\r\n\tModule Name: ${row.MODULE} 
\r\n\trow: ${index + 2}`;
                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else {
                  setWith(
                    newSavedObject,
                    ["pages", row.PAGE, moduleId, item.toString(), row.FIELD],
                    row.TRANSLATION,
                    Object
                  );

                  const moduleWithSubItemsName = get(base, ["pages", row.PAGE, moduleId, "moduleName"]);
                  if (moduleWithSubItemsName)
                    setWith(newSavedObject, `pages.${row.PAGE}.${moduleId}.moduleName`, moduleWithSubItemsName, Object);
                }
              }
            } else {
              // CHECK IF MODULE EXISTS
              // eslint-disable-next-line no-lonely-if
              if (!get(base, ["pages", row.PAGE, row.MODULE])) {
                ErrorLog += `\r\n[WARNING] -- I could not find the MODULE: "${
                  row.MODULE
                }" in this configuration, Are you using an old csv export? 
                \r\n\trow: ${index + 2}`;
                ignoredRows.push(index + 2);
                errorFound = true;
              } else {
                const currentBaseText = get(base, `pages.${row.PAGE}.${row.MODULE}.${row.FIELD}`);
                const newBaseText = row.BASE;
                if (!currentBaseText) {
                  ErrorLog += `\r\n[ERROR] -- There is no base for the given key, this might be because you are using an old csv file, or a csv generated for another market
                  \r\n\tPage Name: ${row.PAGE}
                  \r\n\tField Name: ${row.FIELD} 
                  \r\n\tModule Name: ${row.MODULE} 
                  \r\n\trow: ${index + 2}`;
                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else if (currentBaseText.trim().toLowerCase() !== newBaseText.trim().toLowerCase()) {
                  ErrorLog += `\r\n
[ERROR] -- The base text stored as a base for the given key is different, are you sure the file is correct? You might not be saving the file as csv UTF-8 format
\r\n\tOriginal base: ${currentBaseText}
\r\n\tUploaded base text: ${newBaseText}
\r\n\tPage Name: ${row.PAGE}
\r\n\tField Name: ${row.FIELD} 
\r\n\tModule Name: ${row.MODULE} 
\r\n\trow: ${index + 2}`;

                  ignoredRows.push(index + 2);
                  errorFound = true;
                } else {
                  const normalModuleName = get(base, `pages.${row.PAGE}.${row.MODULE}.normalModuleName`);
                  setWith(newSavedObject, `pages.${row.PAGE}.${row.MODULE}.${row.FIELD}`, row.TRANSLATION, Object);
                  if (normalModuleName)
                    setWith(newSavedObject, `pages.${row.PAGE}.${row.MODULE}.moduleName`, normalModuleName, Object);
                }
              }
            }
          }
        });

      if (ignoredRows.length > 0) {
        const old = window.document.getElementById("errorLogLink");
        if (old) old.remove();

        const a = window.document.createElement("a");
        a.id = "errorLogLink";
        a.href = window.URL.createObjectURL(new Blob([ErrorLog], { type: "text/plain;charset=utf-8" }));
        a.download = "ErrorLog.txt";

        // Append anchor to body.
        document.body.appendChild(a);
        // a.click();

        toast.warn(
          <HashLink
            text={`I found some errors, and I had to ignore some rows. The ones not mentioned in the error log were imported. 
                Please, download the error log `}
            a={a}
          />,
          { autoClose: false }
        );
      }
      if (!fatalError) resolve(newSavedObject);
    });
  });

const HashLink = ({ text, a }) => (
  <div>
    <span>{text}</span>
    <Button color="link" onClick={() => a.click()}>
      here
    </Button>
  </div>
);

export const importFromCSV = (file, brand, market, language) =>
  new Promise((resolve, reject) => {
    const config = {
      delimiter: "", // auto-detect
      newline: "", // auto-detect
      quoteChar: '"',
      escapeChar: '"',
      header: true,
      transformHeader: undefined,
      dynamicTyping: false,
      preview: 0,
      encoding: "UTF-8",
      worker: false,
      comments: false,
      step: undefined,
      complete: results => {
        ProcessNewTranslation(results, brand, market)
          .then(newTranslation => {
            resolve(newTranslation);
          })
          .catch(err => reject(err));
      },
      error: error => {
        reject(error);
      },
      download: false,
      downloadRequestHeaders: undefined,
      skipEmptyLines: true,
      chunk: undefined,
      fastMode: undefined,
      beforeFirstChunk: undefined,
      withCredentials: undefined,
      transform: undefined,
      delimitersToGuess: [",", "\t", "|", ";", Papa.RECORD_SEP, Papa.UNIT_SEP]
    };

    Papa.parse(file, config);
  });

export const convertToTranslationObject = configFile => {
  const resultObject = {};
  configFile.global.modules
    .filter(item => !!item.instanceId)
    .forEach(item => {
      Object.keys(item.config.translations).forEach(key => {
        if (!resultObject.global) resultObject.global = {};
        if (!resultObject.global[item.instanceId]) {
          resultObject.global[item.instanceId] = {};
          resultObject.global[item.instanceId].moduleName = item.name;
        }
        resultObject.global[item.instanceId][key] = item.config.translations[key];
      });
    });

  configFile.pages
    .filter(page => page.title && page.modules)
    .forEach(page => {
      if (!resultObject.pages) resultObject.pages = {};
      if (!resultObject.pages[page.title]) resultObject.pages[page.title] = {};

      page.modules
        .filter(individualModule => individualModule.instanceId && individualModule.name)
        .forEach(individualModule => {
          if (!resultObject.pages[page.title][individualModule.instanceId]) {
            resultObject.pages[page.title][individualModule.instanceId] = {};

            resultObject.pages[page.title][individualModule.instanceId].moduleName = individualModule.name;
          }

          if (individualModule.config.translations) {
            Object.keys(individualModule.config.translations).forEach(key => {
              resultObject.pages[page.title][individualModule.instanceId][key] =
                individualModule.config.translations[key];
            });
          } else if (
            individualModule.config.items &&
            individualModule.config.items.filter(item => !!item.trasnslations)
          ) {
            individualModule.config.items.forEach((item, index) => {
              if (!resultObject.pages[page.title][individualModule.instanceId][index])
                resultObject.pages[page.title][individualModule.instanceId][index] = {};

              Object.keys(item.translations).forEach(key => {
                resultObject.pages[page.title][individualModule.instanceId][index][key] = item.translations[key];
              });
            });
          }
        });
    });
  return resultObject;
};

export const convertFromTranslationObjectToConfigFormat = (translationObject, originalTranslationConfig) => {
  const modTranslationConfig = Object.assign({}, originalTranslationConfig);

  // GLOBAL
  if (translationObject.global)
    Object.keys(translationObject.global).forEach(moduleInstanceId => {
      Object.keys(translationObject.global[moduleInstanceId])
        .filter(key => key !== "moduleName")
        .forEach(key => {
          const foundModule = modTranslationConfig.global.modules.find(
            individualModule => individualModule.instanceId === moduleInstanceId
          );
          if (!foundModule)
            toast.warn(
              `I could not find the following module on the base config: ${
                translationObject.global[moduleInstanceId].moduleName
              } (${moduleInstanceId}) that should be located in Global`,
              {
                toastId: moduleInstanceId
              }
            );
          else foundModule.config.translations[key] = translationObject.global[moduleInstanceId][key];
        });
    });

  // PAGES
  if (translationObject.pages)
    Object.keys(translationObject.pages).forEach(pageName => {
      Object.keys(translationObject.pages[pageName]).forEach(moduleInstanceId => {
        Object.keys(translationObject.pages[pageName][moduleInstanceId])
          .filter(key => key !== "moduleName")
          .forEach(key => {
            if (typeof translationObject.pages[pageName][moduleInstanceId][key] === "string") {
              const foundPage = modTranslationConfig.pages.find(individualPage => individualPage.title === pageName);

              if (!foundPage) toast.warn(`I could not find the following page on the base config: ${pageName}`);
              else {
                const foundModule = foundPage.modules.find(
                  individualModule => individualModule.instanceId === moduleInstanceId
                );
                if (!foundModule)
                  toast.warn(
                    `I could not find the following module on the base config: ${
                      translationObject.pages[pageName][moduleInstanceId].moduleName
                    } (${moduleInstanceId}) that should be located in the page ${pageName}`
                  );
                else foundModule.config.translations[key] = translationObject.pages[pageName][moduleInstanceId][key];
              }
            } else {
              Object.keys(translationObject.pages[pageName][moduleInstanceId][key])
                .filter(item => item !== "moduleName")
                .forEach(item => {
                  const foundPage = modTranslationConfig.pages.find(
                    individualPage => individualPage.title === pageName
                  );
                  if (!foundPage) toast.warn(`I could not find the following page on the base config: ${pageName}`);
                  else {
                    const foundModule = foundPage.modules.find(
                      individualModule => individualModule.instanceId === moduleInstanceId
                    );
                    if (!foundModule) {
                      toast.warn(
                        `I could not find the following module on the base config: ${
                          translationObject.pages[pageName][moduleInstanceId].moduleName
                        } (${moduleInstanceId}) that should be located in the page ${pageName}`
                      );
                    } else if (!foundModule.config.items[key]) {
                      toast.warn(
                        `I could not find the item ${key} in the module on the base config: ${
                          translationObject.pages[pageName][moduleInstanceId].moduleName
                        } (${moduleInstanceId}) that is located in the page ${pageName}`
                      );
                    } else {
                      foundModule.config.items[key].translations[item] =
                        translationObject.pages[pageName][moduleInstanceId][key][item];
                    }
                  }
                });
            }
          });
      });
    });
  return modTranslationConfig;
};

export const getUserProfile = email => {
  const docReference = db.collection(`/users`).doc(email);
  return docReference.get();
};
